/**
 * CÓMO SE SUBE UN DOCUMENTO (Omnicanal → catálogo de documentos)
 * ==============================================================
 * Por documento del catálogo se elige cómo lo sube el colaborador en la liga y
 * qué formatos se aceptan. Se guarda en `InputCatalog.settings` (el JSON en
 * texto que ya trae aiType/aiPrompt/downloadUrl), sin campos nuevos en el modelo:
 *   captura:     'AMBAS' (foto o archivo, como siempre) | 'CAMARA' (solo foto
 *                con la cámara del equipo) | 'ARCHIVO' (solo archivo)
 *   extensiones: ['pdf', 'jpg', 'png'] (los que el sistema puede analizar hoy)
 *   guia:        'TARJETA' (recuadro horizontal de identificación, p. ej. la
 *                INE) | 'HOJA' (recuadro vertical de hoja carta) — la liga abre
 *                su cámara con ese recuadro y sube la foto recortada a él
 * Sin configurar = como siempre: foto o archivo, PDF/JPG/PNG, sin recuadro.
 *
 * ESTA REGLA ES LA MISMA en el panel (omnichannel-config/data/helpers/
 * carga-documento.ts), la liga (aquí) y los servidores
 * (pulpi-v4-app-backend app/services/cargaDocumento.js, pulpi-v4-backend
 * app/helpers/cargaDocumento.js), con la tabla cargaDocumento.casos.json.
 * La `guia` solo la leen el panel y la liga (es de pantalla: el servidor recibe
 * una imagen como cualquier otra), por eso no está en los servidores ni en la tabla.
 *
 * «Solo cámara» en la liga abre la cámara del celular (capture); en una
 * computadora el navegador puede ofrecer igual elegir un archivo: el servidor
 * solo puede asegurar que sea imagen, no de dónde salió.
 */

export type ModoCaptura = 'AMBAS' | 'CAMARA' | 'ARCHIVO';
export type ExtensionDocumento = 'pdf' | 'jpg' | 'png';
export type GuiaCamara = 'TARJETA' | 'HOJA';

export const MODOS_CAPTURA: readonly ModoCaptura[] = ['AMBAS', 'CAMARA', 'ARCHIVO'];
export const EXTENSIONES_DOCUMENTO: readonly ExtensionDocumento[] = ['pdf', 'jpg', 'png'];
export const GUIAS_CAMARA: readonly GuiaCamara[] = ['TARJETA', 'HOJA'];
const EXTENSIONES_IMAGEN: readonly ExtensionDocumento[] = ['jpg', 'png'];

const MIMES: Record<ExtensionDocumento, string[]> = {
  pdf: ['application/pdf'],
  jpg: ['image/jpeg', 'image/jpg'],
  png: ['image/png'],
};
const SUFIJOS: Record<ExtensionDocumento, string[]> = { pdf: ['.pdf'], jpg: ['.jpg', '.jpeg'], png: ['.png'] };
const NOMBRES: Record<ExtensionDocumento, string> = { pdf: 'PDF', jpg: 'JPG', png: 'PNG' };

export interface IReglasCarga {
  captura: ModoCaptura;
  extensiones: ExtensionDocumento[];
  /** Tipos MIME aceptados (lo que valida el servidor). */
  mimes: string[];
  /** Para el atributo `accept` del input: MIME y sufijos. */
  accept: string;
  /** Se puede tomar foto (hay modo cámara y algún formato de imagen). */
  conCamara: boolean;
  /** Se puede elegir un archivo. */
  conArchivo: boolean;
  /** Recuadro de la cámara de la liga; null sin recuadro (o si no hay cámara). */
  guia: GuiaCamara | null;
}

function leerSettings(settings: unknown): Record<string, unknown> {
  if (settings && typeof settings === 'object') return settings as Record<string, unknown>;
  if (typeof settings !== 'string' || !settings.trim()) return {};
  try {
    const v = JSON.parse(settings);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

/** Las reglas de carga de un documento a partir de su `settings` (texto JSON u objeto). */
export function reglasDeCarga(settings: unknown): IReglasCarga {
  const s = leerSettings(settings);
  const captura: ModoCaptura = MODOS_CAPTURA.includes(s['captura'] as ModoCaptura) ? (s['captura'] as ModoCaptura) : 'AMBAS';
  const pedidas = Array.isArray(s['extensiones'])
    ? (s['extensiones'] as unknown[]).map((e) => String(e ?? '').trim().toLowerCase().replace(/^\./, '').replace(/^jpeg$/, 'jpg'))
    : [];
  let extensiones = EXTENSIONES_DOCUMENTO.filter((e) => pedidas.includes(e));
  if (!extensiones.length) extensiones = [...EXTENSIONES_DOCUMENTO];
  // Con la cámara solo salen fotos: el PDF no aplica.
  if (captura === 'CAMARA') {
    extensiones = extensiones.filter((e) => EXTENSIONES_IMAGEN.includes(e));
    if (!extensiones.length) extensiones = [...EXTENSIONES_IMAGEN];
  }
  const mimes = extensiones.flatMap((e) => MIMES[e]);
  const accept = [...mimes, ...extensiones.flatMap((e) => SUFIJOS[e])].join(',');
  const conImagen = extensiones.some((e) => EXTENSIONES_IMAGEN.includes(e));
  const conCamara = captura !== 'ARCHIVO' && conImagen;
  const guia = conCamara && GUIAS_CAMARA.includes(s['guia'] as GuiaCamara) ? (s['guia'] as GuiaCamara) : null;
  return { captura, extensiones, mimes, accept, conCamara, conArchivo: captura !== 'CAMARA', guia };
}

/** ¿El archivo entra? Por su tipo MIME; si el navegador no lo dice, por la terminación del nombre. */
export function aceptaArchivo(reglas: IReglasCarga, mimetype: unknown, nombre?: unknown): boolean {
  const mime = String(mimetype ?? '').trim().toLowerCase();
  if (mime && mime !== 'application/octet-stream') return reglas.mimes.includes(mime);
  const n = String(nombre ?? '').trim().toLowerCase();
  return reglas.extensiones.some((e) => SUFIJOS[e].some((suf) => n.endsWith(suf)));
}

/** «PDF, JPG o PNG». */
export function textoFormatos(reglas: Pick<IReglasCarga, 'extensiones'>): string {
  const n = reglas.extensiones.map((e) => NOMBRES[e]);
  return n.length > 1 ? `${n.slice(0, -1).join(', ')} o ${n[n.length - 1]}` : n[0] || '';
}

/** Lo que se guarda en settings: solo lo que no es lo de siempre (sin configurar = foto o archivo, PDF/JPG/PNG, sin recuadro). */
export function settingsDeCarga(
  captura: ModoCaptura,
  extensiones: ExtensionDocumento[],
  guia: GuiaCamara | null = null,
): { captura?: ModoCaptura; extensiones?: ExtensionDocumento[]; guia?: GuiaCamara } {
  const r = reglasDeCarga({ captura, extensiones, guia });
  const todas = reglasDeCarga({ captura }).extensiones;
  return {
    ...(r.captura !== 'AMBAS' ? { captura: r.captura } : {}),
    ...(r.extensiones.join(',') !== todas.join(',') ? { extensiones: r.extensiones } : {}),
    ...(r.guia ? { guia: r.guia } : {}),
  };
}
